import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useParams } from 'react-router';

import { IGlobalStoreState } from '../../../store';
import { IEventStoreState, ISeatStoreState, SeatStoreState } from '../../../store/event/types';
import { IVenueSectorStoreState } from '../../../store/ticket/types';
import { getWidgetEvent } from '../../../store/event/actions';
import { Page, PageContent } from '../../../components/UI';
import { PlaceSelector, SchemaSectorSelector } from '../../../components/book';
import { clearBook, initBook } from '../../../store/book/actions';
import { EventHelper } from '../../../helpers';
import { IBookStoreState } from '../../../store/book/types';
import { AlertType, KeyErrors } from '../../../store/enums';
import { hideAdButton } from '../../../store/header/actions';
import { useTranslation } from 'react-i18next';
import { setResultInfo } from '../../../store/resultInformarion/actions';
import { useRoutesHelper } from '../../../helpers/routesHelper';
import { TicketsCountSelector } from './widgetComponents/ticketsCountSelector';
import { WidgetFooter } from './widgetComponents/widgetFooter';
import { WidgetTicketsAvailabilityPopup } from './widgetComponents/widgetTicketsAvailabilityPopup';
import { AnalyticService } from '../../../services';

const WidgetBookPage = () => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const history = useHistory();

  const event = useSelector<IGlobalStoreState, IEventStoreState>((state) => state.book.event);
  const isLoading = useSelector<IGlobalStoreState, boolean>((state) => state.app.isLoading);
  const book = useSelector<IGlobalStoreState, IBookStoreState>((state) => state.book);
  const isRedirection = useSelector<IGlobalStoreState>((state) => state.app.isRedirection);

  const [isCheckoutInitiating, setIsCheckoutInitiating] = useState(false);
  const [seats, setSeats] = useState<ISeatStoreState[]>([]);
  const [sectorSlug, setSectorSlug] = useState(null);
  const [quotaId, setQuotaId] = useState(null);
  const [showSeatsSelector, setShowSeatsSelector] = useState(false);
  const [showTicketsAvailabilityPopup, setShowTicketsAvailabilityPopup] = useState(false);

  const { getResultStatusRoute } = useRoutesHelper();
  const { token } = useParams<{ token: string }>();
  const analyticService = new AnalyticService();

  useEffect(() => {
    initEvent(token);
    dispatch(hideAdButton);
  }, []);

  // Инициализация Facebook Pixel при загрузке события с facebookPixelId
  useEffect(() => {
    if (event && event.facebookPixelId) {
      analyticService.initializePixel(event.facebookPixelId);
    }
  }, [event?.facebookPixelId]);

  const handleTicketsCount = useCallback(
    (count: number) => {
      const prevSeatsForQuota = seats.filter((s) => s.quotaId === quotaId && s.sectorSlug === sectorSlug);
      const prevCount = prevSeatsForQuota.length;

      let filteredSeatsByQuotaId = seats.filter((s) => s.quotaId !== quotaId);
      for (let index = 0; index < count; index++) {
        const sector = event.sectors.find((x) => x.slug === sectorSlug);
        const newSeat = new SeatStoreState(quotaId, sectorSlug, sector.title);
        filteredSeatsByQuotaId = [...filteredSeatsByQuotaId, newSeat];
      }
      setSeats(filteredSeatsByQuotaId);

      // GTM ecommerce: map quantity change to add/remove events (no specific seat coordinates)
      const sector = event?.sectors?.find((x) => x.slug === sectorSlug);
      if (sector && quotaId && sectorSlug) {
        if (count > prevCount) {
          for (let i = 0; i < count - prevCount; i++) {
            const seat = new SeatStoreState(quotaId, sectorSlug, sector.title);
            analyticService.trackAddToCart({ event, seat });
          }
        } else if (count < prevCount) {
          for (let i = 0; i < prevCount - count; i++) {
            const seat = prevSeatsForQuota[i] || new SeatStoreState(quotaId, sectorSlug, sector.title);
            analyticService.trackRemoveFromCart({ event, seat });
          }
        }
      }
    },
    [seats, quotaId, sectorSlug, event]
  );

  const renderPlaceSelector = (event: IEventStoreState, sector: IVenueSectorStoreState) => {
    const caption = `${t("Book.WidgetRowNumbering")}`;
    if (!event || !sector) return null;
    return (
      <PlaceSelector
        caption={caption}
        venueSlug={event.venue.slug}
        sector={sector}
        selectedSeats={seats}
        maxSelectCount={event.maxPurchase}
        onChange={(quotaId: number, sectorSlug: string, rowSlug: string, seatSlug: string) =>
          handlePlaceSelect(quotaId, sectorSlug, rowSlug, seatSlug)
        }
      />
    );
  };

  const handleSchemaSectorSelect = (sectorSlug: string) => {
    setSectorSlug(sectorSlug);
    setShowSeatsSelector(false);
    const sector = event && event.sectors && event.sectors.find((x) => x.slug === sectorSlug);
    const quota = sector && sector.ticketQuotas[0];
    if (quota !== null) {
      setQuotaId(quota.id);
    }
    setShowSeatsSelector(true);
  };

  const handlePlaceSelect = (quotaId: number, sectorSlug: string, rowSlug: string, seatSlug: string) => {
    if (seats.some((s) => s.sectorSlug === sectorSlug && s.rowSlug === rowSlug && s.seatSlug === seatSlug)) {
      const removedSeat = seats.find((s) => s.sectorSlug === sectorSlug && s.rowSlug === rowSlug && s.seatSlug === seatSlug);
      if (removedSeat) {
        analyticService.trackRemoveFromCart({ event, seat: removedSeat });
      }
      const seatsFiltered = seats.filter(
        (s) => s.sectorSlug !== sectorSlug || s.rowSlug !== rowSlug || s.seatSlug !== seatSlug
      );
      setSeats(seatsFiltered);
      return;
    }
    const sector = event.sectors.find((x) => x.slug === sectorSlug);
    if (book.event.maxPurchase > seats.length) {
      const newSeat = new SeatStoreState(quotaId, sectorSlug, sector.title, rowSlug, seatSlug);
      setSeats([...seats, newSeat]);
      analyticService.trackAddToCart({ event, seat: newSeat });
    }
    return;
  };

  const initEvent = useCallback(
    (token: string) => {
      (async () => dispatch(getWidgetEvent(token)))().then((event: any) => {
        if (!event || !event.slug) {
          dispatch(clearBook());
          dispatch(
            setResultInfo({
              type: AlertType.Error,
              key: KeyErrors.EventNotFound,
              title: 'Alert.SomethingWentWrong',
              message: 'Alert.CheckLinkNoEventFound',
            })
          );
          history.replace(getResultStatusRoute(AlertType.Error, KeyErrors.EventNotFound));
          return;
        }
        dispatch(clearBook());
        dispatch(initBook(event));
      });
    },
    [token]
  );

  if (isRedirection || isLoading || !event || isCheckoutInitiating) return null;

  const sector = event && event.sectors && event.sectors.find((x) => x.slug === sectorSlug);
  const isInteractiveSchema = !!sector && !!sector.schema;

  let posterUrl = event.optionPosterUrl;
  if (posterUrl === '') {
    posterUrl = event.posterUrl;
  }

  return (
    <Page
      isForm
      title={`${t('WidgetBook.TicketPurchase')} | ${event.title} - ${event.subtitle}`}
      description={EventHelper.getMetaDescription(event)}
      keywords={[event.title, event.venue.title, t('WidgetBook.BuyTicket'), t('WidgetBook.Concert')]}
      imageUrl={posterUrl}
    >
      <PageContent>
        {
          <SchemaSectorSelector
            venue={event.venue}
            sectors={event.sectors}
            selectedSector={sectorSlug}
            onChange={(sectorSlug: string) => handleSchemaSectorSelect(sectorSlug)}
          />
        }
        {isInteractiveSchema && showSeatsSelector && renderPlaceSelector(event, sector)}
        {!isInteractiveSchema && (
          <TicketsCountSelector
            handleTicketsCount={handleTicketsCount}
            event={event}
            sector={sector}
            seats={seats}
            quotaId={quotaId}
          />
        )}
      </PageContent>
      <WidgetFooter
        book={book}
        event={event}
        seats={seats}
        sectorSlug={sectorSlug}
        isLoading={isLoading}
        setIsCheckoutInitiating={setIsCheckoutInitiating}
        token={token}
      />
      <WidgetTicketsAvailabilityPopup
        open={showTicketsAvailabilityPopup}
        handleClose={() => setShowTicketsAvailabilityPopup(false)}
      />
    </Page>
  );
};

export default WidgetBookPage;
